import Note from '@/models/note.model';
import Tag from '@/models/tag.model';

import BaseStorageProvider from './base.provider';

export default class LocalStorageProvider extends BaseStorageProvider {
  constructor(store) {
    super();

    this.$store = store;

    this.$store.commit('setStorageProviderReady', true);
  }

  getNoteData() {
    let data = localStorage.getItem('notes');

    if (data) {
      data = JSON.parse(data);
    } else {
      data = [];
    }

    return data.map(note => new Note(note));
  }

  setNoteData(data) {
    localStorage.setItem('notes', JSON.stringify(data));
  }

  getTagData() {
    let data = localStorage.getItem('tags');

    if (data) {
      data = JSON.parse(data);
    } else {
      data = [];
    }

    return data.map(tag => new Tag(tag));
  }

  setTagData(data) {
    localStorage.setItem('tags', JSON.stringify(data));
  }

  getStagedData() {
    let data = localStorage.getItem('stagedNotes');

    if (data) {
      data = JSON.parse(data);
    } else {
      data = [];
    }

    return data.map(note => new Note(note));
  }

  setStagedData(data) {
    localStorage.setItem('stagedNotes', JSON.stringify(data));
  }

  async getNotes({
    offset = 0,
    limit = 10,
    tag = null,
    search = null,
    untagged = false,
    archived = false,
    deleted = false,
    uuidsOnly = false,
  } = {}) {
    if (tag && untagged) {
      throw new Error("'tag' and 'untagged' can't be specified at the same time");
    }

    if (archived && deleted) {
      throw new Error("'archived' and 'deleted' can't be specified at the same time");
    }

    if (offset === undefined) {
      offset = 0;
    }

    if (limit === undefined) {
      limit = 10;
    }

    let notes = this.getNoteData();

    if (tag !== null) {
      notes = notes.filter(note => note.tags.includes(tag));
    }

    if (untagged) {
      notes = notes.filter(note => note.tags.length === 0);
    }

    notes = notes.filter(note => note.archived === archived && note.deleted === deleted);

    if (search !== null) {
      notes = notes.filter(note => {
        const text = Note.getNodeText(note.content).toLowerCase();
        return text.includes(search.toLowerCase());
      });
    }

    notes.sort((a, b) => {
      if (a.updated < b.updated) {
        return 1;
      }

      if (a.updated > b.updated) {
        return -1;
      }

      return 0;
    });

    if (limit !== 0) {
      notes = notes.slice(offset, offset + limit);
    }

    if (uuidsOnly) {
      return notes.map(note => note.uuid);
    }

    return notes;
  }

  async getNote(uuid) {
    let notes = this.getNoteData();
    notes = notes.filter(note => note.uuid === uuid);

    if (notes.length === 0) {
      throw new Error(`Note with uuid ${uuid} doesn't exist`);
    }

    if (notes.length > 1) {
      throw new Error(`More than one result found for uuid ${uuid}`);
    }

    return notes[0];
  }

  async createNote() {
    const notes = this.getNoteData();
    const note = new Note();
    notes.push(note);
    this.setNoteData(notes);

    return note;
  }

  async updateNote(note) {
    const notes = this.getNoteData();
    for (let i = 0; i < notes.length; i++) {
      if (notes[i].uuid === note.uuid) {
        notes[i] = note;
        break;
      }
    }

    this.setNoteData(notes);
  }

  async deleteNote(uuid) {
    let notes = this.getNoteData();
    notes = notes.filter(note => note.uuid !== uuid);
    this.setNoteData(notes);
  }

  async restoreDeletedNotes() {
    let notes = this.getNoteData();
    notes = notes.map(note => {
      note.deleted = false;
      return note;
    });
    this.setNoteData(notes);
  }

  async purgeDeletedNotes() {
    let notes = this.getNoteData();
    notes = notes.filter(note => !note.deleted);
    this.setNoteData(notes);
  }

  async getStagedNotes() {
    return this.getStagedData();
  }

  stageNote(note) {
    const stagedNotes = this.getStagedData();
    let exists = false;
    for (let i = 0; i < stagedNotes.length; i++) {
      if (stagedNotes[i].uuid === note.uuid) {
        stagedNotes[i] = note;
        exists = true;
        break;
      }
    }

    if (!exists) {
      stagedNotes.push(note);
    }

    this.setNoteData(stagedNotes);
  }

  clearStagedNotes() {
    this.setStagedData([]);
  }

  deleteStagedNote(uuid) {
    let stagedNotes = this.getStagedData();
    stagedNotes = stagedNotes.filter(note => note.uuid !== uuid);
    this.setStagedData(stagedNotes);
  }

  async getNotesRaw() {
    return this.getNoteData();
  }

  async setNotesRaw(notes) {
    this.setNoteData(notes);
  }

  async getTags() {
    const tags = this.getTagData();

    tags.sort((a, b) => {
      if (a.name > b.name) {
        return 1;
      }

      if (a.name < b.name) {
        return -1;
      }

      return 0;
    });

    return tags;
  }

  async getTag(name) {
    let tags = this.getTagData();
    tags = tags.filter(tag => tag.name === name);

    if (tags.length === 0) {
      return null;
    }

    if (tags.length > 1) {
      throw new Error(`More than one result found for name ${name}`);
    }

    return tags[0];
  }

  async createTag(name) {
    const tagExists = await this.getTag(name);

    if (tagExists) {
      return;
    }

    const tags = this.getTagData();
    const tag = new Tag({ name });
    tags.push(tag);
    this.setTagData(tags);
  }

  async updateTag(tag) {
    const tags = this.getTagData();
    for (let i = 0; i < tags.length; i++) {
      if (tags[i].name === tag.name) {
        tags[i] = tag;
        break;
      }
    }

    this.setTagData(tags);
  }

  async deleteTag(name) {
    const notes = this.getNoteData();
    const count = notes.filter(note => note.tags.includes(name)).length;

    if (count !== 0) {
      return;
    }

    let tags = this.getTagData();
    tags = tags.filter(tag => tag.name !== name);
    this.setTagData(tags);
  }

  async getTagsRaw() {
    return this.getTagData();
  }

  async setTagsRaw(tags) {
    this.setTagData(tags);
  }

  throwImageError() {
    const message = 'Local images are not supported when using Local Storage';

    // eslint-disable-next-line no-alert
    alert(message);
    throw new Error(message);
  }

  getImages() {
    this.throwImageError();
  }

  getImage() {
    this.throwImageError();
  }

  createImage() {
    this.throwImageError();
  }

  deleteNoteImages() {
    this.throwImageError();
  }

  deleteImage() {
    this.throwImageError();
  }

  getImagesRaw() {
    this.throwImageError();
  }

  setImagesRaw() {
    this.throwImageError();
  }
}
